import { useEffect, useMemo, useState } from "react";

export interface Pagination<T> {
  /** 1-based current page. */
  page: number;
  setPage: (page: number) => void;
  /** Total number of pages (at least 1). */
  pageCount: number;
  /** The items on the current page. */
  pageItems: T[];
  /** 0-based index of the first item on the current page. */
  start: number;
  /** Total number of items across all pages. */
  total: number;
  pageSize: number;
}

/**
 * Client-side pagination over an in-memory list.
 *
 * Slices `items` into pages of `pageSize` and tracks the current page. The page
 * is clamped whenever the list shrinks (e.g. after re-filtering) so it never
 * points past the end.
 */
export function usePagination<T>(items: T[], pageSize: number): Pagination<T> {
  const [page, setPage] = useState(1);

  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));

  // Reset to the first page when the page size changes (e.g. via the selector).
  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const start = (page - 1) * pageSize;
  const pageItems = useMemo(
    () => items.slice(start, start + pageSize),
    [items, start, pageSize],
  );

  return {
    page,
    setPage,
    pageCount,
    pageItems,
    start,
    total: items.length,
    pageSize,
  };
}
